import { formatMoney } from "../timeTracking/utils";
import { PORTAL_PATH, PORTAL_REQUEST_PATH, PORTAL_SPEND_PATH } from "./routes";
import type {
  CaseTypeOption,
  PortalEmployee,
  PortalFeatures,
  PortalRequest,
  RequestForm,
  RequestInput,
  WaitingOn
} from "./types";

export type PortalTab = { path: string; label: string };

/** Header tabs for what this contact may use. Contacts who only sign or
 * approve land on Employees, which then shows just what needs them. */
export const portalTabs = (f: PortalFeatures): PortalTab[] => [
  { path: PORTAL_PATH, label: f.employees ? "Employees" : "Needs you" },
  ...(f.requests
    ? [{ path: PORTAL_REQUEST_PATH, label: "Request a case" }]
    : []),
  ...(f.spend ? [{ path: PORTAL_SPEND_PATH, label: "Spend" }] : [])
];

/** Where to send a contact who opened a screen they can't use, or null. */
export const redirectFor = (
  pathname: string,
  f: PortalFeatures
): string | null => {
  if (pathname.startsWith(PORTAL_REQUEST_PATH) && !f.requests)
    return PORTAL_PATH;
  if (pathname.startsWith(PORTAL_SPEND_PATH) && !f.spend) return PORTAL_PATH;
  return null;
};

export const greeting = (name: string, now: Date = new Date()): string => {
  const h = now.getHours();
  const part = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
  return `Good ${part}, ${name.split(" ")[0] || name}`;
};

export const waitingLabel = (w: WaitingOn, firmName: string): string =>
  ({
    company: "You",
    employee: "Employee",
    firm: firmName,
    uscis: "USCIS",
    nobody: "–"
  }[w]);

export const estimateText = (t: Pick<CaseTypeOption, "basis" | "amount">) => {
  if (!t.basis) return "Quoted by the firm";
  if (t.basis === "hourly") return "Hourly";
  const amount = formatMoney(t.amount ?? 0);
  return t.basis === "flat" ? `${amount} flat` : `Hourly, up to ${amount}`;
};

export const emptyRequest = (form: RequestForm | undefined): RequestInput => ({
  employee_name: "",
  employee_email: "",
  job_description_id: "",
  job_title: "",
  worksite_id: "",
  cost_center: "",
  entity_id:
    form?.entities.find((e) => e.is_default)?.id ?? form?.entities[0]?.id ?? "",
  case_type: "",
  po: "",
  client_matter_id: "",
  notes: ""
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type RequestErrors = Partial<Record<keyof RequestInput, string>>;

/** Mirrors the server's checks so the form can say what's missing up front. */
export const validateRequest = (
  input: RequestInput,
  form: RequestForm
): RequestErrors => {
  const errors: RequestErrors = {};
  if (!input.employee_name.trim()) errors.employee_name = "Add their name";
  if (!EMAIL.test(input.employee_email.trim()))
    errors.employee_email = "Add a work email";
  if (!input.case_type) errors.case_type = "Pick what they need";
  if (form.requires_po && !input.po.trim()) errors.po = "Add the PO number";
  if (form.requires_client_matter_id && !input.client_matter_id.trim())
    errors.client_matter_id = "Add your matter ID";
  return errors;
};

export const nextSteps = (
  form: Pick<RequestForm, "you_approve" | "approver_names">,
  firmName: string,
  employeeName: string,
  companyName: string
): string[] => [
  form.you_approve
    ? `You're an approver for ${companyName}, so sending approves it.`
    : `${
        form.approver_names.join(" or ") || "Your approver"
      } approves it first.`,
  `${firmName} confirms the case type and opens it, usually within one business day.`,
  `${
    employeeName.split(" ")[0] || "The employee"
  } gets a secure questionnaire and document list.`,
  "You'll see status here and get an email at each USCIS step."
];

export const requestStatusText = (r: PortalRequest, firmName: string) =>
  ({
    pending_approval: "Waiting on approval",
    pending_firm: `With ${firmName}`,
    accepted: "Accepted · intake sent to the employee",
    declined: "Declined"
  }[r.status]);

export const filterEmployees = (
  rows: PortalEmployee[],
  costCenter: string | null,
  waiting: WaitingOn | null
): PortalEmployee[] =>
  rows.filter(
    (r) =>
      (!costCenter || r.cost_center === costCenter) &&
      (!waiting || r.waiting_on === waiting)
  );
